import {
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  HStack,
  Badge,
} from '@chakra-ui/react';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaMinusCircle,
  FaArrowRight,
  FaWhatsapp,
  FaRobot,
  FaMobileAlt,
  FaMoneyBillWave,
  FaEye,
  FaLock,
  FaUsers,
  FaChartLine,
  FaExclamationTriangle,
  FaQuoteLeft,
} from 'react-icons/fa';
import { headingFont } from 'theme/tokens';
import { translate } from 'i18n';
import MigrationTimeline from '../components/MigrationTimeline';
import WhatsAppCta from '../components/WhatsAppCta';

/* ─── comparison table data ─── */

type Support = 'yes' | 'no' | 'partial';

interface ComparisonRow {
  featureKey: string;
  mss: Support;
  doinsport: Support;
  mssNoteKey?: string;
  doinsportNoteKey?: string;
}

const COMPARISON: ComparisonRow[] = [
  { featureKey: 'switch_feat_transparent_pricing', mss: 'yes', doinsport: 'no', doinsportNoteKey: 'switch_feat_transparent_pricing_ds_note' },
  { featureKey: 'switch_feat_free_trial', mss: 'yes', doinsport: 'no' },
  { featureKey: 'switch_feat_whatsapp_ai', mss: 'yes', doinsport: 'no', mssNoteKey: 'switch_feat_whatsapp_ai_mss_note' },
  { featureKey: 'switch_feat_white_label_app', mss: 'yes', doinsport: 'yes' },
  { featureKey: 'switch_feat_online_booking', mss: 'yes', doinsport: 'yes' },
  { featureKey: 'switch_feat_prepayment', mss: 'yes', doinsport: 'yes' },
  { featureKey: 'switch_feat_multi_location', mss: 'yes', doinsport: 'yes' },
  { featureKey: 'switch_feat_roi_calculator', mss: 'yes', doinsport: 'no' },
  { featureKey: 'switch_feat_wallet_credits', mss: 'yes', doinsport: 'partial', doinsportNoteKey: 'switch_feat_wallet_credits_ds_note' },
  { featureKey: 'switch_feat_court_access', mss: 'partial', doinsport: 'yes', mssNoteKey: 'switch_feat_court_access_mss_note' },
  { featureKey: 'switch_feat_coach_module', mss: 'yes', doinsport: 'yes' },
  { featureKey: 'switch_feat_events_tournaments', mss: 'yes', doinsport: 'yes' },
  { featureKey: 'switch_feat_membership_mgmt', mss: 'yes', doinsport: 'yes' },
  { featureKey: 'switch_feat_africa_support', mss: 'yes', doinsport: 'no', mssNoteKey: 'switch_feat_africa_support_mss_note' },
];

/* ─── pain point cards ─── */

const PAIN_POINTS = [
  { icon: FaEye, titleKey: 'switch_pain_opaque_title', bodyKey: 'switch_pain_opaque_body', color: 'red.500', bg: 'red.50' },
  { icon: FaLock, titleKey: 'switch_pain_locked_title', bodyKey: 'switch_pain_locked_body', color: 'orange.500', bg: 'orange.50' },
  { icon: FaUsers, titleKey: 'switch_pain_demo_title', bodyKey: 'switch_pain_demo_body', color: 'purple.500', bg: 'purple.50' },
  { icon: FaChartLine, titleKey: 'switch_pain_no_data_title', bodyKey: 'switch_pain_no_data_body', color: 'blue.500', bg: 'blue.50' },
] as const;

/* ─── differentiator cards ─── */

const DIFFERENTIATORS = [
  { icon: FaRobot, titleKey: 'switch_diff_ai_title', bodyKey: 'switch_diff_ai_body', color: 'brand.500', bg: 'brand.50' },
  { icon: FaMoneyBillWave, titleKey: 'switch_diff_pricing_title', bodyKey: 'switch_diff_pricing_body', color: 'accent.500', bg: 'green.50' },
  { icon: FaMobileAlt, titleKey: 'switch_diff_trial_title', bodyKey: 'switch_diff_trial_body', color: 'purple.500', bg: 'purple.50' },
  { icon: FaWhatsapp, titleKey: 'switch_diff_whatsapp_title', bodyKey: 'switch_diff_whatsapp_body', color: '#25D366', bg: 'green.50' },
] as const;

/* ─── helpers ─── */

function SupportIcon({ support }: { support: Support }) {
  if (support === 'yes') return <Icon as={FaCheckCircle} color="accent.500" boxSize={5} />;
  if (support === 'no') return <Icon as={FaTimesCircle} color="red.400" boxSize={5} />;
  return <Icon as={FaMinusCircle} color="orange.400" boxSize={5} />;
}

function SupportCell({ support, noteKey }: { support: Support; noteKey?: string }) {
  return (
    <Td textAlign="center">
      <VStack spacing={1}>
        <SupportIcon support={support} />
        {noteKey && (
          <Text fontSize="xs" color="gray.500">
            {translate(noteKey as any)}
          </Text>
        )}
      </VStack>
    </Td>
  );
}

/* ─── page ─── */

export default function SwitchFromDoinsportPage() {
  return (
    <Box>
      {/* Hero */}
      <Box
        bg="linear-gradient(135deg, #f8fafc 0%, #fff5f5 50%, #f0fdf4 100%)"
        px={{ base: 4, md: 8 }}
        pt={{ base: 12, md: 20 }}
        pb={{ base: 8, md: 14 }}
      >
        <VStack maxW="750px" mx="auto" spacing={5} textAlign="center">
          <Badge
            bg="red.50"
            color="red.600"
            fontSize="xs"
            fontWeight="600"
            px={3}
            py={1}
            borderRadius="full"
            textTransform="none"
          >
            {translate('marketing.switch_badge' as any)}
          </Badge>
          <Text
            fontFamily={headingFont}
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="800"
            lineHeight="1.1"
            letterSpacing="-0.03em"
            color="gray.900"
          >
            {translate('marketing.switch_hero_title' as any)}
          </Text>
          <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="580px">
            {translate('marketing.switch_hero_subtitle' as any)}
          </Text>
          <Flex
            direction={{ base: 'column', sm: 'row' }}
            gap={3}
            pt={2}
          >
            <WhatsAppCta />
            <Button
              as="a"
              href="#/marketing/roi-calculator"
              variant="outline"
              size="lg"
              borderRadius="full"
              borderColor="gray.300"
              color="gray.700"
              fontWeight="600"
              rightIcon={<Icon as={FaArrowRight} boxSize={3} />}
              _hover={{ borderColor: 'brand.500', color: 'brand.500' }}
            >
              {translate('marketing.switch_cta_calculator' as any)}
            </Button>
          </Flex>
        </VStack>
      </Box>

      {/* Pain points — why clubs switch */}
      <Box px={{ base: 4, md: 8 }} py={{ base: 8, md: 14 }}>
        <VStack maxW="900px" mx="auto" spacing={8}>
          <VStack spacing={3} textAlign="center">
            <Text fontFamily={headingFont} fontWeight="700" fontSize={{ base: 'xl', md: '2xl' }} color="gray.800">
              {translate('marketing.switch_pain_title' as any)}
            </Text>
            <Text fontSize="sm" color="gray.500" maxW="540px">
              {translate('marketing.switch_pain_subtitle' as any)}
            </Text>
          </VStack>
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
            gap={6}
            w="100%"
          >
            {PAIN_POINTS.map((p, i) => (
              <HStack
                key={i}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="2xl"
                p={6}
                spacing={4}
                align="flex-start"
              >
                <Flex
                  w="44px"
                  h="44px"
                  minW="44px"
                  borderRadius="xl"
                  bg={p.bg}
                  align="center"
                  justify="center"
                >
                  <Icon as={p.icon} color={p.color} boxSize={5} />
                </Flex>
                <Box>
                  <Text fontFamily={headingFont} fontWeight="700" fontSize="sm" color="gray.800" mb={1}>
                    {translate(`marketing.${p.titleKey}` as any)}
                  </Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="1.7">
                    {translate(`marketing.${p.bodyKey}` as any)}
                  </Text>
                </Box>
              </HStack>
            ))}
          </Grid>
        </VStack>
      </Box>

      {/* Feature comparison table */}
      <Box bg="gray.50" px={{ base: 4, md: 8 }} py={{ base: 8, md: 14 }}>
        <VStack maxW="800px" mx="auto" spacing={8}>
          <VStack spacing={3} textAlign="center">
            <Text fontFamily={headingFont} fontWeight="700" fontSize={{ base: 'xl', md: '2xl' }} color="gray.800">
              {translate('marketing.switch_compare_title' as any)}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {translate('marketing.switch_compare_subtitle' as any)}
            </Text>
          </VStack>
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="2xl"
            overflow="hidden"
            w="100%"
            shadow="sm"
          >
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr bg="gray.50">
                    <Th
                      fontFamily={headingFont}
                      fontWeight="700"
                      fontSize="xs"
                      color="gray.600"
                      textTransform="none"
                      py={4}
                      pl={6}
                    >
                      {translate('marketing.switch_table_feature' as any)}
                    </Th>
                    <Th
                      textAlign="center"
                      fontFamily={headingFont}
                      fontWeight="700"
                      fontSize="xs"
                      textTransform="none"
                      py={4}
                      w="120px"
                    >
                      <Text
                        bgGradient="linear(to-r, brand.500, accent.500)"
                        bgClip="text"
                      >
                        MySportSet
                      </Text>
                    </Th>
                    <Th
                      textAlign="center"
                      fontFamily={headingFont}
                      fontWeight="700"
                      fontSize="xs"
                      color="gray.500"
                      textTransform="none"
                      py={4}
                      w="120px"
                    >
                      Doinsport
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {COMPARISON.map((row, i) => (
                    <Tr key={i} _hover={{ bg: 'gray.50' }}>
                      <Td pl={6} py={4}>
                        <Text fontSize="sm" color="gray.700" fontWeight="500">
                          {translate(`marketing.${row.featureKey}` as any)}
                        </Text>
                      </Td>
                      <SupportCell support={row.mss} noteKey={row.mssNoteKey ? `marketing.${row.mssNoteKey}` : undefined} />
                      <SupportCell support={row.doinsport} noteKey={row.doinsportNoteKey ? `marketing.${row.doinsportNoteKey}` : undefined} />
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
          <Text fontSize="xs" color="gray.400" textAlign="center" maxW="600px">
            {translate('marketing.switch_compare_disclaimer' as any)}
          </Text>
        </VStack>
      </Box>

      {/* Key differentiators */}
      <Box px={{ base: 4, md: 8 }} py={{ base: 8, md: 14 }}>
        <VStack maxW="900px" mx="auto" spacing={8}>
          <VStack spacing={3} textAlign="center">
            <Text fontFamily={headingFont} fontWeight="700" fontSize={{ base: 'xl', md: '2xl' }} color="gray.800">
              {translate('marketing.switch_diff_title' as any)}
            </Text>
          </VStack>
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
            gap={6}
            w="100%"
          >
            {DIFFERENTIATORS.map((d, i) => (
              <VStack
                key={i}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="2xl"
                p={8}
                spacing={4}
                align="flex-start"
              >
                <Flex
                  w="48px"
                  h="48px"
                  borderRadius="xl"
                  bg={d.bg}
                  align="center"
                  justify="center"
                >
                  <Icon as={d.icon} color={d.color} boxSize={5} />
                </Flex>
                <Text fontFamily={headingFont} fontWeight="700" fontSize="md" color="gray.800">
                  {translate(`marketing.${d.titleKey}` as any)}
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="1.7">
                  {translate(`marketing.${d.bodyKey}` as any)}
                </Text>
              </VStack>
            ))}
          </Grid>
        </VStack>
      </Box>

      {/* Testimonial / quote */}
      <Box bg="gray.50" px={{ base: 4, md: 8 }} py={{ base: 8, md: 14 }}>
        <Box
          maxW="700px"
          mx="auto"
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="2xl"
          p={{ base: 6, md: 10 }}
          shadow="sm"
          position="relative"
        >
          <Icon
            as={FaQuoteLeft}
            color="brand.100"
            boxSize={8}
            position="absolute"
            top={6}
            left={6}
          />
          <VStack spacing={4} align="flex-start" pl={{ base: 0, md: 6 }} pt={4}>
            <Text fontSize="md" color="gray.700" fontStyle="italic" lineHeight="1.8">
              {translate('marketing.switch_testimonial_quote' as any)}
            </Text>
            <Box>
              <Text fontFamily={headingFont} fontWeight="700" fontSize="sm" color="gray.800">
                {translate('marketing.switch_testimonial_name' as any)}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {translate('marketing.switch_testimonial_role' as any)}
              </Text>
            </Box>
          </VStack>
        </Box>
      </Box>

      {/* Migration timeline */}
      <Box px={{ base: 4, md: 8 }} py={{ base: 8, md: 14 }}>
        <VStack maxW="800px" mx="auto" spacing={8}>
          <VStack spacing={3} textAlign="center">
            <Text fontFamily={headingFont} fontWeight="700" fontSize={{ base: 'xl', md: '2xl' }} color="gray.800">
              {translate('marketing.switch_migration_title' as any)}
            </Text>
            <Text fontSize="sm" color="gray.500" maxW="540px">
              {translate('marketing.switch_migration_subtitle' as any)}
            </Text>
          </VStack>
          <MigrationTimeline />
        </VStack>
      </Box>

      {/* Risk reversal */}
      <Box bg="brand.500" px={{ base: 4, md: 8 }} py={{ base: 8, md: 12 }}>
        <VStack maxW="600px" mx="auto" spacing={4} textAlign="center">
          <Icon as={FaExclamationTriangle} color="whiteAlpha.700" boxSize={6} />
          <Text fontFamily={headingFont} fontWeight="800" fontSize={{ base: 'lg', md: 'xl' }} color="white">
            {translate('marketing.switch_risk_title' as any)}
          </Text>
          <Text fontSize="sm" color="whiteAlpha.800" lineHeight="1.7">
            {translate('marketing.switch_risk_body' as any)}
          </Text>
        </VStack>
      </Box>

      {/* Final CTA */}
      <Box px={{ base: 4, md: 8 }} py={14} textAlign="center">
        <VStack spacing={5}>
          <Text fontFamily={headingFont} fontWeight="700" fontSize="xl" color="gray.800">
            {translate('marketing.switch_final_cta' as any)}
          </Text>
          <Text fontSize="sm" color="gray.500" maxW="480px">
            {translate('marketing.switch_final_cta_sub' as any)}
          </Text>
          <Flex
            direction={{ base: 'column', sm: 'row' }}
            gap={4}
            align="center"
          >
            <WhatsAppCta />
            <Button
              as="a"
              href="#/auth/sign-up"
              bg="brand.500"
              color="white"
              size="lg"
              borderRadius="full"
              fontWeight="600"
              rightIcon={<Icon as={FaArrowRight} boxSize={3} />}
              _hover={{ bg: 'brand.600' }}
            >
              {translate('marketing.switch_cta_signup' as any)}
            </Button>
          </Flex>
        </VStack>
      </Box>
    </Box>
  );
}
